import { useQueryClient } from "@tanstack/vue-query";

/**
 * Composable for clearing both TanStack Query and Vuex cache
 * Use this for user settings and refresh functionality
 */
export function useCacheClear() {
  const queryClient = useQueryClient();

  // Helper to clear Vuex store cache (if using Vuex caching)
  const clearVuexCache = () => {
    // Access the global dataCache from various modules
    if (typeof window !== "undefined") {
      if (window.__VUEX_DATA_CACHE__) {
        window.__VUEX_DATA_CACHE__.clear(); // transfers
      }
      if (window.__VUEX_INVENTORY_CACHE__) {
        window.__VUEX_INVENTORY_CACHE__.clear(); // inventory
      }
      if (window.__VUEX_PRODUCTS_CACHE__) {
        window.__VUEX_PRODUCTS_CACHE__.clear(); // products
      }
      if (window.__VUEX_PURCHASES_CACHE__) {
        window.__VUEX_PURCHASES_CACHE__.clear(); // purchases
      }
      if (window.__VUEX_GROUPS_CACHE__) {
        window.__VUEX_GROUPS_CACHE__.clear(); // groups
      }
    }
  };

  return {
    /**
     * Clear all cached data (TanStack Query + Vuex)
     */
    clearAllCache: () => {
      queryClient.clear();
      clearVuexCache();
      console.log("🗑️ All cache cleared");
    },

    /**
     * Clear specific resource caches
     */
    clearTransferCache: () => {
      queryClient.removeQueries({ queryKey: ["transfers"] });
      queryClient.removeQueries({ queryKey: ["transferRequests"] });
      console.log("🗑️ Transfer cache cleared");
    },

    clearMappingCache: () => {
      queryClient.removeQueries({ queryKey: ["productMappings"] });
      queryClient.removeQueries({ queryKey: ["mappingSuggestions"] });
      console.log("🗑️ Mapping cache cleared");
    },

    clearRemovalCache: () => {
      queryClient.removeQueries({ queryKey: ["removals"] });
      console.log("🗑️ Removal cache cleared");
    },

    clearTicketCache: () => {
      queryClient.removeQueries({ queryKey: ["tickets"] });
      console.log("🗑️ Ticket cache cleared");
    },

    clearInventoryCache: () => {
      queryClient.removeQueries({ queryKey: ["inventory"] });
      if (typeof window !== "undefined" && window.__VUEX_INVENTORY_CACHE__) {
        window.__VUEX_INVENTORY_CACHE__.clear();
      }
      console.log("🗑️ Inventory cache cleared");
    },

    clearProductsCache: () => {
      queryClient.removeQueries({ queryKey: ["products"] });
      queryClient.removeQueries({ queryKey: ["services"] });
      if (typeof window !== "undefined" && window.__VUEX_PRODUCTS_CACHE__) {
        window.__VUEX_PRODUCTS_CACHE__.clear();
      }
      console.log("🗑️ Products cache cleared");
    },

    clearCatalogPurchasesCache: () => {
      queryClient.removeQueries({ queryKey: ["purchases"] });
      if (typeof window !== "undefined" && window.__VUEX_PURCHASES_CACHE__) {
        window.__VUEX_PURCHASES_CACHE__.clear();
      }
      console.log("🗑️ Catalog purchases cache cleared");
    },

    clearGroupsCache: () => {
      queryClient.removeQueries({ queryKey: ["groups"] });
      if (typeof window !== "undefined" && window.__VUEX_GROUPS_CACHE__) {
        window.__VUEX_GROUPS_CACHE__.clear();
      }
      console.log("🗑️ Groups cache cleared");
    },

    /**
     * Get cache statistics
     */
    getCacheStats: () => {
      const cache = queryClient.getQueryCache();
      const queries = cache.getAll();

      // Get Vuex cache sizes and categorize by cache key
      const vuexTransfersCache = window.__VUEX_DATA_CACHE__ || new Map();
      const vuexInventoryCache = window.__VUEX_INVENTORY_CACHE__ || new Map();
      const vuexProductsCache = window.__VUEX_PRODUCTS_CACHE__ || new Map();
      const vuexPurchasesCache = window.__VUEX_PURCHASES_CACHE__ || new Map();
      const vuexGroupsCache = window.__VUEX_GROUPS_CACHE__ || new Map();

      // Categorize transfers cache by key prefix
      let transfersCount = 0;
      let transferRequestsCount = 0;
      let removalsCount = 0;

      for (const key of vuexTransfersCache.keys()) {
        if (key.startsWith("transfers-")) {
          transfersCount++;
        } else if (key.startsWith("transfer-requests-")) {
          transferRequestsCount++;
        } else if (key.startsWith("removals-")) {
          removalsCount++;
        }
      }

      const totalVuexCache =
        vuexTransfersCache.size +
        vuexInventoryCache.size +
        vuexProductsCache.size +
        vuexPurchasesCache.size +
        vuexGroupsCache.size;

      return {
        totalQueries: queries.length + totalVuexCache,
        tanstackQueries: queries.length,
        vuexCache: totalVuexCache,
        byType: {
          transfers:
            queries.filter((q) => q.queryKey[0] === "transfers").length +
            transfersCount,
          transferRequests:
            queries.filter((q) => q.queryKey[0] === "transferRequests").length +
            transferRequestsCount,
          mappings: queries.filter((q) => q.queryKey[0] === "productMappings")
            .length,
          removals:
            queries.filter((q) => q.queryKey[0] === "removals").length +
            removalsCount,
          tickets: queries.filter((q) => q.queryKey[0] === "tickets").length,
          inventory: vuexInventoryCache.size,
          products: vuexProductsCache.size,
          purchases: vuexPurchasesCache.size,
          groups: vuexGroupsCache.size,
        },
      };
    },
  };
}
